import { findFlightEvaluationsByCriteria } from '@src/main/modules/reports/flight/application/use-cases/find-flight-evaluations-by-criteria.usecase';
import { createFlightEvaluationHttpRepository } from '@src/main/modules/reports/flight/infrastructure/repository/FlightEvaluationHttpRepository';
import {
  BriefFlightEvaluation,
  EvaluationCriteria,
} from '@src/main/modules/reports/shared/domain/evaluation.interface';
import { EvaluationFilterMeta } from '@src/main/modules/reports/ui/mapEvaluationFiltersToCriteria';
import { useAuth } from '@src/main/shared/context/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { DataTablePageEvent, DataTableSortEvent } from 'primereact/datatable';
import { useCallback, useMemo, useState } from 'react';

export function useFlightEvaluations(
  initialFilters: EvaluationFilterMeta,
  mapFiltersToCriteria: (
    filters: EvaluationFilterMeta,
    emptyCriteria: EvaluationCriteria,
  ) => EvaluationCriteria,
  emptyCriteria: EvaluationCriteria,
) {
  const { user } = useAuth();
  const [filters, setFilters] = useState<EvaluationFilterMeta>(initialFilters);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string>('flightDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const getToken = useCallback(async () => user?.token ?? '', [user?.token]);
  const tokenProvider = useMemo(() => ({ getToken }), [getToken]);

  const repository = useMemo(
    () => (user?.token ? createFlightEvaluationHttpRepository(tokenProvider) : null),
    [tokenProvider, user?.token],
  );

  const resetPage = useCallback(() => setPageNumber(0), []);

  const criteria = useMemo(() => {
    const base = mapFiltersToCriteria(filters, emptyCriteria);
    return {
      ...base,
      offset: pageNumber * pageSize,
      limit: pageSize,
      sortField,
      sortOrder,
    };
  }, [filters, emptyCriteria, pageNumber, pageSize, sortField, sortOrder, mapFiltersToCriteria]);

  const { data, isLoading, refetch, isFetching } = useQuery<{
    items: BriefFlightEvaluation[];
    total: number;
  }>({
    queryKey: ['flightEvaluations', criteria],
    queryFn: async () => {
      if (!repository) throw new Error('Repository not available');
      return await findFlightEvaluationsByCriteria(repository, criteria);
    },
    enabled: !!repository,
    placeholderData: (prev) => prev,
    staleTime: 1000 * 60,
  });

  const handlePageChange = useCallback((e: DataTablePageEvent) => {
    setPageNumber(Math.floor(e.first / e.rows));
    setPageSize(e.rows);
  }, []);

  const handleSort = useCallback((event: DataTableSortEvent) => {
    setSortField(event.sortField ?? 'flightDate');
    setSortOrder(event.sortOrder === 1 ? 'asc' : 'desc');
  }, []);

  return {
    filters,
    setFilters,
    evaluations: data?.items ?? [],
    total: data?.total ?? 0,
    isLoading,
    isFetching,
    pageNumber,
    pageSize,
    handlePageChange,
    resetPage,
    refetchEvaluations: refetch,
    handleSort,
    sortField,
    sortOrder,
  };
}
